import type { RecordingMode, SavedTake } from "./types";

const DB_NAME = "speakai-recorder";
const DB_VERSION = 1;
const TAKES_STORE = "takes";
const CHUNKS_STORE = "chunks";
const TAKE_LOCK_PREFIX = "speakai-recorder-take:";
const TAKE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const MIN_FREE_BYTES: Record<RecordingMode, number> = {
  audio: 10 * 1024 * 1024,
  video: 200 * 1024 * 1024,
  screen: 200 * 1024 * 1024,
};

const signedOutUserIds = new Set<string>();

interface StoredChunk {
  takeId: string;
  seq: number;
  data: ArrayBuffer;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      request.result.createObjectStore(TAKES_STORE, { keyPath: "id" });
      request.result.createObjectStore(CHUNKS_STORE, { keyPath: ["takeId", "seq"] });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function withTransaction<T>(
  mode: IDBTransactionMode,
  work: (transaction: IDBTransaction) => T | Promise<T>,
): Promise<T> {
  const database = await openDatabase();
  try {
    return await new Promise<T>((resolve, reject) => {
      const transaction = database.transaction([TAKES_STORE, CHUNKS_STORE], mode);
      const result = work(transaction);
      transaction.oncomplete = () => resolve(result);
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  } finally {
    database.close();
  }
}

function chunkRange(takeId: string): IDBKeyRange {
  return IDBKeyRange.bound([takeId, 0], [takeId, Infinity]);
}

function deleteTakeIn(transaction: IDBTransaction, takeId: string): void {
  transaction.objectStore(TAKES_STORE).delete(takeId);
  transaction.objectStore(CHUNKS_STORE).delete(chunkRange(takeId));
}

function readAll<T>(transaction: IDBTransaction, storeName: string, query?: IDBKeyRange): Promise<T[]> {
  return new Promise((resolve, reject) => {
    const request = transaction.objectStore(storeName).getAll(query);
    request.onsuccess = () => resolve(request.result as T[]);
    request.onerror = () => reject(request.error);
  });
}

async function heldTakeLocks(): Promise<Set<string>> {
  if (typeof navigator === "undefined" || !navigator.locks) return new Set();
  const { held = [] } = await navigator.locks.query();
  return new Set(held.map((lock) => lock.name ?? ""));
}

export function isTakeStoreAvailable(): boolean {
  return typeof indexedDB !== "undefined";
}

/** True when the browser reports room for a take of this mode, or does not report its space at all. */
export async function hasSpaceForTake(mode: RecordingMode): Promise<boolean> {
  const estimate = await navigator.storage?.estimate?.().catch(() => undefined);
  if (!estimate?.quota) return true;
  return estimate.quota - (estimate.usage ?? 0) >= MIN_FREE_BYTES[mode];
}

/** Asks the browser not to evict saved takes under storage pressure; the answer is ignored. */
export function requestPersistentStorage(): void {
  void navigator.storage?.persist?.().catch(() => undefined);
}

/** Writes the take row and, in the same transaction, `chunks` numbered from `firstSeq`. */
export async function saveTake(take: SavedTake, chunks: Blob[] = [], firstSeq = 0): Promise<void> {
  if (signedOutUserIds.has(take.userId)) return;
  // Bytes, not Blobs: Safari has lost Blobs stored in IndexedDB, and a transaction cannot wait for blob.arrayBuffer().
  const buffers = await Promise.all(chunks.map((blob) => blob.arrayBuffer()));
  await withTransaction("readwrite", (transaction) => {
    transaction.objectStore(TAKES_STORE).put(take);
    const chunkStore = transaction.objectStore(CHUNKS_STORE);
    buffers.forEach((data, index) => {
      chunkStore.put({ takeId: take.id, seq: firstSeq + index, data } satisfies StoredChunk);
    });
  });
}

/** Returns the take's saved chunks in recording order. */
export function loadTakeChunks(takeId: string): Promise<Blob[]> {
  // A cursor turns one chunk at a time into a Blob, so a multi-GB take is never held as ArrayBuffers all at once.
  return withTransaction("readonly", (transaction) => {
    const blobs: Blob[] = [];
    const request = transaction.objectStore(CHUNKS_STORE).openCursor(chunkRange(takeId));
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) return;
      blobs.push(new Blob([(cursor.value as StoredChunk).data]));
      cursor.continue();
    };
    return blobs;
  });
}

/** Builds the take's file from its saved chunks. */
export async function loadTakeBlob(take: SavedTake): Promise<Blob> {
  const chunks = await loadTakeChunks(take.id);
  return new Blob(chunks, { type: take.mimeType || chunks[0]?.type || "" });
}

/** Deletes the take row and all of its chunks. */
export function deleteTake(takeId: string): Promise<void> {
  return withTransaction("readwrite", (transaction) => deleteTakeIn(transaction, takeId));
}

/** Lets `userId`'s takes be saved again after a sign-out in this page load blocked them. */
export function allowTakeSaving(userId: string): void {
  signedOutUserIds.delete(userId);
}

/** Deletes every take saved by `userId` and blocks further saves for them until allowTakeSaving. */
export async function deleteUserTakes(userId: string): Promise<void> {
  signedOutUserIds.add(userId);
  if (!isTakeStoreAvailable()) return;
  const takes = await listAllTakes();
  const ids = takes.filter((take) => take.userId === userId).map((take) => take.id);
  if (ids.length === 0) return;
  await withTransaction("readwrite", (transaction) => ids.forEach((id) => deleteTakeIn(transaction, id)));
}

function listAllTakes(): Promise<SavedTake[]> {
  return withTransaction("readonly", (transaction) => readAll<SavedTake>(transaction, TAKES_STORE));
}

/** Deletes every user's takes older than 7 days, then returns `userId`'s takes no open tab holds, newest first. */
export async function listRecoverableTakes(userId: string, now = Date.now()): Promise<SavedTake[]> {
  if (!isTakeStoreAvailable()) return [];
  const takes = await listAllTakes();
  const held = await heldTakeLocks();
  const expired = takes.filter(
    (take) => now - take.startedAt > TAKE_MAX_AGE_MS && !held.has(TAKE_LOCK_PREFIX + take.id),
  );
  if (expired.length > 0) {
    await withTransaction("readwrite", (transaction) => expired.forEach((take) => deleteTakeIn(transaction, take.id)));
  }
  return takes
    .filter((take) => !expired.includes(take) && take.userId === userId && !held.has(TAKE_LOCK_PREFIX + take.id))
    .sort((a, b) => b.startedAt - a.startedAt);
}

/** Marks the take as in use by this tab until the returned release runs or the tab closes. */
export function holdTakeLock(takeId: string): () => void {
  if (typeof navigator === "undefined" || !navigator.locks) return () => {};
  let release: () => void = () => {};
  const held = new Promise<void>((resolve) => {
    release = resolve;
  });
  void navigator.locks.request(TAKE_LOCK_PREFIX + takeId, () => held).catch(() => undefined);
  return release;
}
